// Zeit und Datum in Europe/Berlin (fachliche Tagesgrenzen wie in API.md), unabhängig von der Zeitzone des Rechners.
const TZ = "Europe/Berlin";

const teileFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

interface Teile {
  j: number;
  m: number;
  t: number;
  h: number;
  mi: number;
  s: number;
}

function teile(d: Date): Teile {
  const p: Record<string, string> = {};
  for (const x of teileFormat.formatToParts(d)) p[x.type] = x.value;
  return { j: +p.year, m: +p.month, t: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
}

const zwei = (n: number) => String(n).padStart(2, "0");

/** Minuten, die Berlin zum gegebenen Zeitpunkt vor UTC liegt (60 oder 120). */
function versatz(d: Date): number {
  const p = teile(d);
  return Math.round((Date.UTC(p.j, p.m - 1, p.t, p.h, p.mi, p.s) - d.getTime()) / 60000);
}

export function gueltig(iso: string | null | undefined): iso is string {
  return !!iso && !Number.isNaN(new Date(iso).getTime());
}

/** Heutiges Datum in Berlin als YYYY-MM-DD. */
export function heute(): string {
  const p = teile(new Date());
  return `${p.j}-${zwei(p.m)}-${zwei(p.t)}`;
}

/** Datum + n Tage (YYYY-MM-DD). */
export function tagPlus(datum: string, n: number): string {
  const [j, m, t] = datum.slice(0, 10).split("-").map(Number);
  const d = new Date(Date.UTC(j, m - 1, t + n));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

/** Wochentag 0 = Sonntag … 6 = Samstag für ein YYYY-MM-DD. */
export function wochentag(datum: string): number {
  const [j, m, t] = datum.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, t)).getUTCDay();
}

/** Nächster Montag (ab morgen). */
export function naechsterMontag(von = heute()): string {
  const wt = wochentag(von);
  return tagPlus(von, wt === 1 ? 7 : (8 - wt) % 7 || 7);
}

/** Tage zwischen zwei YYYY-MM-DD (b - a). */
export function tageZwischen(a: string, b: string): number {
  const [j1, m1, t1] = a.slice(0, 10).split("-").map(Number);
  const [j2, m2, t2] = b.slice(0, 10).split("-").map(Number);
  return Math.round((Date.UTC(j2, m2 - 1, t2) - Date.UTC(j1, m1 - 1, t1)) / 86400000);
}

/** ISO-Zeitpunkt -> YYYY-MM-DD in Berlin. */
export function berlinDatum(iso: string): string {
  const p = teile(new Date(iso));
  return `${p.j}-${zwei(p.m)}-${zwei(p.t)}`;
}

/** ISO-Zeitpunkt -> {datum: YYYY-MM-DD, zeit: HH:mm} in Berlin. */
export function berlinTeile(iso: string): { datum: string; zeit: string } {
  const p = teile(new Date(iso));
  return { datum: `${p.j}-${zwei(p.m)}-${zwei(p.t)}`, zeit: `${zwei(p.h)}:${zwei(p.mi)}` };
}

/** Datum + Uhrzeit in Berlin -> ISO in UTC. */
export function berlinZuIso(datum: string, zeit: string): string {
  const [j, m, t] = datum.split("-").map(Number);
  const [h, mi] = zeit.split(":").map(Number);
  const geschaetzt = Date.UTC(j, m - 1, t, h, mi);
  let ms = geschaetzt - versatz(new Date(geschaetzt)) * 60000;
  const zweiter = versatz(new Date(ms));
  ms = geschaetzt - zweiter * 60000;
  return new Date(ms).toISOString().replace(".000Z", "+00:00");
}

const WOCHENTAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const MONATE = ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."];

/** YYYY-MM-DD -> 20.10.2026 */
export function datumText(datum: string | null | undefined): string {
  if (!datum) return "";
  const [j, m, t] = datum.slice(0, 10).split("-");
  if (!j || !m || !t) return datum;
  return `${t}.${m}.${j}`;
}

/** ISO -> 20.10.2026, 14:05 (Berlin) */
export function zeitpunktText(iso: string | null | undefined, mitJahr = true): string {
  if (!gueltig(iso)) return iso ?? "";
  const p = teile(new Date(iso));
  return `${zwei(p.t)}.${zwei(p.m)}.${mitJahr ? p.j : ""}${mitJahr ? "," : ""} ${zwei(p.h)}:${zwei(p.mi)}`;
}

/** ISO -> „Di 13.10.2026, 14:05“ für Tooltips. */
export function zeitpunktLang(iso: string | null | undefined): string {
  if (!gueltig(iso)) return "";
  const d = new Date(iso);
  const p = teile(d);
  const wt = WOCHENTAGE[wochentag(`${p.j}-${zwei(p.m)}-${zwei(p.t)}`)];
  return `${wt} ${zwei(p.t)}.${zwei(p.m)}.${p.j}, ${zwei(p.h)}:${zwei(p.mi)} Uhr`;
}

/** Kurzer Tagesname relativ zu heute: Heute, Morgen, Gestern, sonst „Mo 20.10.“ (mit Jahr, wenn anders). */
export function tagText(datum: string): string {
  const d = tageZwischen(heute(), datum);
  if (d === 0) return "Heute";
  if (d === 1) return "Morgen";
  if (d === -1) return "Gestern";
  if (d === 2) return "Übermorgen";
  const [j, m, t] = datum.slice(0, 10).split("-");
  const wt = WOCHENTAGE[wochentag(datum)];
  return `${wt} ${t}.${m}.${j}`; // Datum überall gleich: TT.MM.JJJJ (Design-Runde 2, K)
}

/** Relative Angabe: „gerade eben“, „vor 5 Min.“, „in 2 Std.“, „gestern“, „vor 3 Tagen“, „vor 2 Monaten“. */
export function relativ(iso: string | null | undefined, jetzt = Date.now()): string {
  if (!gueltig(iso)) return "";
  const t = new Date(iso).getTime();
  const diff = jetzt - t;
  const abs = Math.abs(diff);
  const zukunft = diff < 0;
  const min = Math.round(abs / 60000);
  if (abs < 45_000) return "gerade eben";
  if (min < 60) return zukunft ? `in ${min} Min.` : `vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 6) return zukunft ? `in ${std} Std.` : `vor ${std} Std.`;
  const tage = tageZwischen(berlinDatum(new Date(jetzt).toISOString()), berlinDatum(iso));
  if (tage === 0) return zukunft ? `in ${std} Std.` : `vor ${std} Std.`;
  if (tage === -1) return "gestern";
  if (tage === 1) return "morgen";
  const at = Math.abs(tage);
  if (at < 14) return tage < 0 ? `vor ${at} Tagen` : `in ${at} Tagen`;
  if (at < 60) {
    const w = Math.round(at / 7);
    return tage < 0 ? `vor ${w} Wochen` : `in ${w} Wochen`;
  }
  if (at < 365) {
    const mo = Math.round(at / 30);
    return tage < 0 ? `vor ${mo} Monaten` : `in ${mo} Monaten`;
  }
  const jahre = Math.round(at / 365);
  return tage < 0 ? `vor ${jahre} ${jahre === 1 ? "Jahr" : "Jahren"}` : `in ${jahre} ${jahre === 1 ? "Jahr" : "Jahren"}`;
}

/** Monatsname kurz für Überschriften. */
export function monatText(datum: string): string {
  const [j, m] = datum.split("-").map(Number);
  return `${MONATE[m - 1]} ${j}`;
}

/** Dauer in Sekunden -> „2:35 Min.“ bzw. „45 Sek.“ */
export function dauerText(sek: number | null | undefined): string {
  if (sek === null || sek === undefined) return "";
  if (sek < 60) return `${sek} Sek.`;
  const h = Math.floor(sek / 3600);
  const m = Math.floor((sek % 3600) / 60);
  const s = sek % 60;
  return h ? `${h}:${zwei(m)}:${zwei(s)} Std.` : `${m}:${zwei(s)} Min.`;
}

/** Fälligkeit einer Aufgabe: Text + ob überfällig/heute. `date` ist Datum oder ISO-Zeitpunkt. */
export function faelligkeit(date: string | null, dueAt: string | null): { text: string; ueberfaellig: boolean; heute: boolean; zeit: string | null } {
  if (!date && !dueAt) return { text: "Ohne Datum", ueberfaellig: false, heute: false, zeit: null };
  const mitZeit = !!date && date.length > 10;
  const tag = mitZeit && gueltig(date) ? berlinDatum(date) : (date ?? (dueAt ? berlinDatum(dueAt) : heute())).slice(0, 10);
  const zeit = mitZeit && gueltig(date) ? berlinTeile(date).zeit : null;
  const d = tageZwischen(heute(), tag);
  let ueberfaellig = d < 0;
  if (d === 0 && mitZeit && date && new Date(date).getTime() < Date.now()) ueberfaellig = true;
  return { text: tagText(tag) + (zeit ? `, ${zeit}` : ""), ueberfaellig, heute: d === 0, zeit };
}
